import { HttpTypes } from "@medusajs/types"

export type CatalogFilterKey =
  | "brand"
  | "category"
  | "subcategory"
  | "architecture"
  | "wired"
  | "poe"
  | "sfp"
  | "ros"

export type CatalogFilters = Partial<Record<CatalogFilterKey, string>>

export type ProductSpecChip = {
  key: string
  label: string
  value: string
}

export type ProductHighlight = {
  label: string
  icon?: string
}

export type ProductSpecRow = {
  label: string
  value: string
}

export type ProductSpecGroup = {
  title: string
  rows: ProductSpecRow[]
}

export type ProductDocument = {
  title: string
  url: string
  type?: string
}

const SPEC_FIELDS: Array<
  Exclude<CatalogFilterKey, "brand" | "category" | "subcategory">
> = ["architecture", "wired", "poe", "sfp", "ros"]

const FILTER_FIELDS: CatalogFilterKey[] = [
  "brand",
  "category",
  "subcategory",
  ...SPEC_FIELDS,
]

function formatBrandLabel(raw: string): string {
  const key = raw.trim().toLowerCase()
  const labels: Record<string, string> = {
    mikrotik: "MikroTik",
    ruijie: "Ruijie",
    reyee: "Reyee",
    ubiquiti: "Ubiquiti",
    unifi: "UniFi",
  }
  return labels[key] || raw.trim()
}

function formatCategoryLabel(raw: string): string {
  const key = raw.trim().toLowerCase().replace(/_/g, "-")
  const labels: Record<string, string> = {
    wireless: "Wireless",
    wifi: "WiFi",
    switches: "Switches",
    routers: "Routers",
    "ethernet-routers": "Ethernet Routers",
    reyee: "Home Wi-Fi",
    "home-wifi": "Home Wi-Fi",
    "wireless-bridge": "Wireless Bridge",
    security: "Security",
    accessories: "Accessories",
  }
  return (
    labels[key] ||
    raw
      .trim()
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase())
  )
}

function formatSubcategoryLabel(raw: string): string {
  const key = raw.trim().toLowerCase().replace(/_/g, "-")
  const labels: Record<string, string> = {
    "ceiling-ap": "Ceiling AP",
    "wall-mount-ap": "Wall-Mount AP",
    "outdoor-ap": "Outdoor AP",
    accessories: "Accessories",
    "unmanaged-switch": "Unmanaged Switch",
    "smart-cctv-switch": "Smart CCTV Switch",
    "l2-managed-switch": "L2 Managed Switch",
    "l3-managed-switch": "L3 Managed Switch",
    "industrial-switch": "Industrial Switch",
    "eg-series": "EG Series",
    "nbr-series": "NBR Series",
    "wireless-router": "Wireless Router",
    "mesh-router": "Mesh Router",
    "wifi-router": "Wi-Fi Router",
    "wifi-extender": "Wi-Fi Extender",
    "4g-wifi-router": "4G Wi-Fi Router",
    "airmetro-series": "AirMetro Series",
    "est-series": "EST Series",
    firewalls: "Firewalls",
  }
  return (
    labels[key] ||
    raw
      .trim()
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase())
  )
}

export function getProductBrand(product: HttpTypes.StoreProduct): string {
  const metadata = metadataOf(product)
  const fromMeta = text(metadata.brand)
  if (fromMeta) return formatBrandLabel(fromMeta)

  const handle = product.handle || ""
  if (/^mikrotik-/i.test(handle)) return "MikroTik"
  if (/^ruijie-/i.test(handle)) return "Ruijie"
  if (/^ubnt-|^unifi-|^ubiquiti-/i.test(handle)) return "Ubiquiti"
  return ""
}

export function getProductCategory(product: HttpTypes.StoreProduct): string {
  const metadata = metadataOf(product)
  const fromMeta = text(metadata.category)
  return fromMeta ? formatCategoryLabel(fromMeta) : ""
}

export function getProductSubcategory(product: HttpTypes.StoreProduct): string {
  const metadata = metadataOf(product)
  const label = text(metadata.subcategory_label)
  if (label) return label
  const slug = text(metadata.subcategory)
  return slug ? formatSubcategoryLabel(slug) : ""
}

function metadataOf(product: HttpTypes.StoreProduct) {
  return (product.metadata || {}) as Record<string, unknown>
}

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : ""
}

function csv(value: unknown): string[] {
  return text(value)
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
}

function parseJson<T>(value: unknown, fallback: T): T {
  if (typeof value !== "string" || !value.trim()) {
    return fallback
  }

  try {
    return JSON.parse(value) as T
  } catch {
    return fallback
  }
}

function slugifyHandlePart(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
}

function usable(value: string): boolean {
  return Boolean(value && value !== "unknown" && value !== "none")
}

/** Language/region picker junk scraped from some vendor PDPs (e.g. Ruijie). */
const LANGUAGE_HIGHLIGHT_JUNK =
  /\b(china|france|germany|italy|poland|spain|indonesia|kazakhstan|t[uü]rkiye|turkey|thailand|deutsch|fran[cç]ais|italiano|polski|espa[nñ]ol|bahasa|indonesian|русский|简体中文|ภาษาไทย|türkçe|turkce)\b/i

function isJunkHighlightLabel(label: string): boolean {
  const text = label.trim()
  if (!text) return true
  if (LANGUAGE_HIGHLIGHT_JUNK.test(text)) return true
  // "Country / Language" pattern
  if (
    /^[A-Za-zÀ-ÿ\u0400-\u04FF\u4e00-\u9fff\s]+\/\s*.+$/.test(text) &&
    text.length < 40
  ) {
    return true
  }
  return false
}

function firstParagraph(value?: string | null): string {
  return (value || "")
    .split("\n")
    .map((line) => line.trim())
    .find((line) => line && !line.startsWith("ข้อมูลจากผู้ผลิต:")) || ""
}

function legacyKeywordChips(keywords: string[]): ProductSpecChip[] {
  const checks: Array<[RegExp, ProductSpecChip]> = [
    [/sfp\+?/i, { key: "sfp", label: "SFP/SFP+", value: "SFP/SFP+" }],
    [/\bpoe\b/i, { key: "poe", label: "PoE", value: "PoE" }],
    [/\barm\b|arm64/i, { key: "architecture", label: "ARM", value: "ARM" }],
    [/routeros|ros\s*v?7|v7/i, { key: "ros", label: "ROS", value: "ROS v7" }],
    [/2\.5g/i, { key: "wired", label: "2.5G", value: "2.5G" }],
    [/\b10g\b/i, { key: "wired", label: "10G", value: "10G" }],
  ]

  const joined = keywords.join(" ")
  return checks
    .filter(([pattern]) => pattern.test(joined))
    .map(([, chip]) => chip)
}

export function getProductModel(product: HttpTypes.StoreProduct): string {
  return product.title?.replace(/^MikroTik\s+/i, "") || product.title || ""
}

export function getProductSummary(product: HttpTypes.StoreProduct): string {
  const metadata = metadataOf(product)
  return (
    text(metadata.short_spec_th) ||
    text(metadata.official_summary) ||
    firstParagraph(product.description)
  )
}

export function getProductSubtitle(product: HttpTypes.StoreProduct): string {
  const metadata = metadataOf(product)
  return text(metadata.official_subtitle) || getProductModel(product)
}

export function getProductCode(product: HttpTypes.StoreProduct): string {
  return text(metadataOf(product).product_code)
}

export function getOfficialSourceUrl(product: HttpTypes.StoreProduct): string {
  return text(metadataOf(product).source_url)
}

export function getProductHighlights(
  product: HttpTypes.StoreProduct
): ProductHighlight[] {
  const metadata = metadataOf(product)
  const highlights = parseJson<ProductHighlight[]>(metadata.highlights_json, [])

  if (highlights.length) {
    const cleaned = highlights.filter(
      (item) => text(item.label) && !isJunkHighlightLabel(item.label)
    )
    if (cleaned.length) return cleaned
  }

  const features = getProductFeatures(product)
  if (features.length) {
    return features.map((label) => ({ label }))
  }

  return getProductSpecChips(product, 4).map((chip) => ({
    label: `${chip.label}: ${chip.value}`,
    icon: chip.key,
  }))
}

export function getProductSpecGroups(
  product: HttpTypes.StoreProduct
): ProductSpecGroup[] {
  const metadata = metadataOf(product)
  const groups = parseJson<ProductSpecGroup[]>(metadata.spec_groups_json, [])

  if (groups.length) {
    return groups
      .map((group) => ({
        title: text(group.title),
        rows: (group.rows || [])
          .map((row) => ({
            label: text(row.label),
            value: text(row.value),
          }))
          .filter((row) => row.label && row.value),
      }))
      .filter((group) => group.title && group.rows.length)
  }

  const specs = getProductSpecs(product)
  const fallbackRows = Object.entries(specs).map(([key, value]) => ({
    label: key.toUpperCase(),
    value: value || "",
  }))

  if (!fallbackRows.length) {
    return []
  }

  return [{ title: "Specifications", rows: fallbackRows }]
}

export function getProductDocuments(
  product: HttpTypes.StoreProduct
): ProductDocument[] {
  const metadata = metadataOf(product)
  return parseJson<ProductDocument[]>(metadata.documents_json, []).filter(
    (doc) => text(doc.title) && text(doc.url)
  )
}

export function getRelatedSkus(product: HttpTypes.StoreProduct): string[] {
  return csv(metadataOf(product).related_skus)
}

export function getIncludedParts(product: HttpTypes.StoreProduct): string[] {
  return csv(metadataOf(product).included_parts)
}

export function getProductOverviewHtml(product: HttpTypes.StoreProduct): string {
  const metadata = metadataOf(product)
  const overview = text(metadata.overview_html)
  if (overview) return overview

  const description = (product.description || "")
    .split("\n")
    .filter((line) => line.trim() && !line.startsWith("ข้อมูลจากผู้ผลิต:"))
    .join("\n")

  if (!description) return ""

  return `<p>${description.replace(/\n/g, "<br />")}</p>`
}

/** Raw brand slug from metadata/handle (mikrotik, ruijie, dlink, ubiquiti). */
export function getProductBrandKey(product: HttpTypes.StoreProduct): string {
  const fromMeta = text(metadataOf(product).brand).toLowerCase()
  if (fromMeta) {
    if (fromMeta === "unifi" || fromMeta === "ubnt") return "ubiquiti"
    return fromMeta.replace(/[^a-z0-9]+/g, "")
  }

  const handle = product.handle || ""
  if (/^mikrotik-/i.test(handle)) return "mikrotik"
  if (/^ruijie-/i.test(handle)) return "ruijie"
  if (/^dlink-/i.test(handle)) return "dlink"
  if (/^ubnt-|^unifi-|^ubiquiti-/i.test(handle)) return "ubiquiti"
  return ""
}

export function getProductCategoryKey(product: HttpTypes.StoreProduct): string {
  return text(metadataOf(product).category)
    .toLowerCase()
    .replace(/_/g, "-")
}

export function getProductSubcategoryKey(product: HttpTypes.StoreProduct): string {
  return text(metadataOf(product).subcategory)
    .toLowerCase()
    .replace(/_/g, "-")
}

/**
 * Prefer leaf Medusa categories (child among the product's assigned set),
 * then fall back to every assigned category id.
 */
export function getPreferredCategoryIds(
  product: HttpTypes.StoreProduct
): string[] {
  const categories = product.categories || []
  if (!categories.length) return []

  const assignedIds = new Set(
    categories.map((category) => category.id).filter(Boolean)
  )
  const leaves = categories.filter(
    (category) =>
      category.id &&
      category.parent_category_id &&
      assignedIds.has(category.parent_category_id)
  )

  if (leaves.length) {
    return leaves.map((category) => category.id).filter(Boolean) as string[]
  }

  return categories.map((category) => category.id).filter(Boolean) as string[]
}

export function skuToHandle(sku: string, brandKey = "mikrotik"): string {
  const brand = slugifyHandlePart(brandKey || "mikrotik") || "mikrotik"
  return slugifyHandlePart(`${brand}-${sku}`)
}

export function productMatchesRelatedSku(
  product: HttpTypes.StoreProduct,
  sku: string,
  brandKey?: string
): boolean {
  const brand = brandKey || getProductBrandKey(product) || "mikrotik"
  const handle = skuToHandle(sku, brand)
  if (product.handle === handle) return true

  const skuSlug = slugifyHandlePart(sku)
  if (!skuSlug) return false

  if (product.handle?.endsWith(`-${skuSlug}`)) return true

  return Boolean(
    product.variants?.some((variant) => {
      const variantSku = slugifyHandlePart(variant.sku || "")
      return variantSku === skuSlug || variantSku.endsWith(`-${skuSlug}`)
    })
  )
}

/**
 * Higher score = more similar. Used to rank "สินค้าที่เกี่ยวข้อง".
 * Official related_skus get a large boost when they resolve in-catalog.
 */
export function scoreRelatedProduct(
  product: HttpTypes.StoreProduct,
  candidate: HttpTypes.StoreProduct,
  relatedSkus: string[] = []
): number {
  if (!candidate?.id || candidate.id === product.id) {
    return -1
  }

  let score = 0
  const brandKey = getProductBrandKey(product)
  const candidateBrand = getProductBrandKey(candidate)

  if (
    relatedSkus.some((sku) =>
      productMatchesRelatedSku(candidate, sku, brandKey)
    )
  ) {
    score += 100
  }

  const productCategoryIds = new Set(
    (product.categories || []).map((category) => category.id).filter(Boolean)
  )
  const sharedCategories = (candidate.categories || []).filter((category) =>
    category.id ? productCategoryIds.has(category.id) : false
  )
  if (sharedCategories.length) {
    score += 40 + Math.min(sharedCategories.length, 2) * 5
  }

  const categoryKey = getProductCategoryKey(product)
  const candidateCategory = getProductCategoryKey(candidate)
  if (categoryKey && categoryKey === candidateCategory) {
    score += 25
  }

  const subcategoryKey = getProductSubcategoryKey(product)
  const candidateSubcategory = getProductSubcategoryKey(candidate)
  if (subcategoryKey && subcategoryKey === candidateSubcategory) {
    score += 30
  }

  if (brandKey && brandKey === candidateBrand) {
    score += 15
  }

  const productSpecs = getProductSpecs(product)
  const candidateSpecs = getProductSpecs(candidate)
  for (const key of SPEC_FIELDS) {
    const left = productSpecs[key]
    const right = candidateSpecs[key]
    if (left && right && left === right) {
      score += 4
    }
  }

  return score
}

export function rankRelatedProducts(
  product: HttpTypes.StoreProduct,
  candidates: HttpTypes.StoreProduct[],
  options: { relatedSkus?: string[]; limit?: number; minScore?: number } = {}
): HttpTypes.StoreProduct[] {
  const relatedSkus = options.relatedSkus ?? getRelatedSkus(product)
  const limit = options.limit ?? 8
  const minScore = options.minScore ?? 15

  return candidates
    .map((candidate) => ({
      candidate,
      score: scoreRelatedProduct(product, candidate, relatedSkus),
    }))
    .filter(({ score }) => score >= minScore)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score
      return (a.candidate.title || "").localeCompare(b.candidate.title || "")
    })
    .slice(0, limit)
    .map(({ candidate }) => candidate)
}

export function getProductBreadcrumb(product: HttpTypes.StoreProduct): string[] {
  const metadata = metadataOf(product)
  const category = text(metadata.category)

  const crumbs = ["HOME", "HARDWARE"]
  if (category) {
    crumbs.push(category.replace(/-/g, " ").toUpperCase())
  }
  return crumbs
}

export function getProductSpecs(product: HttpTypes.StoreProduct): CatalogFilters {
  const metadata = metadataOf(product)

  return SPEC_FIELDS.reduce<CatalogFilters>((acc, key) => {
    const value = text(metadata[key])
    if (usable(value)) {
      acc[key] = value
    }
    return acc
  }, {})
}

export function getProductFeatures(product: HttpTypes.StoreProduct): string[] {
  const metadata = metadataOf(product)
  return csv(metadata.features)
}

export function getProductSpecChips(
  product: HttpTypes.StoreProduct,
  limit = 5
): ProductSpecChip[] {
  const metadata = metadataOf(product)
  const chips: ProductSpecChip[] = [
    { key: "sfp", label: "SFP", value: text(metadata.sfp) },
    { key: "poe", label: "PoE", value: text(metadata.poe) },
    { key: "architecture", label: "CPU", value: text(metadata.architecture) },
    { key: "wired", label: "LAN", value: text(metadata.wired) },
    { key: "ros", label: "OS", value: text(metadata.ros) },
    { key: "wireless", label: "Wi-Fi", value: text(metadata.wireless) },
  ].filter((chip) => usable(chip.value))

  if (chips.length > 0) {
    return chips.slice(0, limit)
  }

  return legacyKeywordChips(csv(metadata.keywords)).slice(0, limit)
}

export function productMatchesFilters(
  product: HttpTypes.StoreProduct,
  filters: CatalogFilters
): boolean {
  const specs = getProductSpecs(product)
  const brand = getProductBrand(product)
  const category = getProductCategory(product)
  const subcategory = getProductSubcategory(product)

  return Object.entries(filters).every(([key, value]) => {
    if (!value) {
      return true
    }
    if (key === "brand") {
      return brand.toLowerCase() === value.toLowerCase()
    }
    if (key === "category") {
      return category.toLowerCase() === value.toLowerCase()
    }
    if (key === "subcategory") {
      return subcategory.toLowerCase() === value.toLowerCase()
    }
    return specs[key as CatalogFilterKey] === value
  })
}

function uniqueSorted(values: Array<string | undefined>): string[] {
  return Array.from(
    new Set(values.filter((value): value is string => Boolean(value)))
  ).sort((a, b) => a.localeCompare(b))
}

/**
 * Cascading / dependent filter options (province → district style).
 * Each dropdown's choices come from products that match every *other*
 * active filter, so Brand=MikroTik narrows Category to MikroTik-only values.
 */
export function getCatalogFilterOptions(
  products: HttpTypes.StoreProduct[],
  activeFilters: CatalogFilters = {}
) {
  return FILTER_FIELDS.reduce<Record<CatalogFilterKey, string[]>>(
    (acc, key) => {
      const siblingFilters: CatalogFilters = { ...activeFilters }
      delete siblingFilters[key]

      const pool = products.filter((product) =>
        productMatchesFilters(product, siblingFilters)
      )

      if (key === "brand") {
        acc.brand = uniqueSorted(pool.map((product) => getProductBrand(product)))
        return acc
      }

      if (key === "category") {
        acc.category = uniqueSorted(
          pool.map((product) => getProductCategory(product))
        )
        return acc
      }

      if (key === "subcategory") {
        acc.subcategory = uniqueSorted(
          pool.map((product) => getProductSubcategory(product))
        )
        return acc
      }

      acc[key] = uniqueSorted(
        pool.map((product) => getProductSpecs(product)[key])
      )
      return acc
    },
    {
      brand: [],
      category: [],
      subcategory: [],
      architecture: [],
      wired: [],
      poe: [],
      sfp: [],
      ros: [],
    }
  )
}
